import { ChevronDown, ChevronRight } from 'lucide-react'
import type { AnchorHTMLAttributes, ReactElement, ReactNode } from 'react'
import { cloneElement, useId } from 'react'
import './branch-row.css'

/**
 * A row in the list of things one question offers — one that opens onto a
 * further question, or one that answers this one.
 *
 * **Both kinds are the same row**, because a reader scanning a stack of them is
 * asking one thing: which of these. The chevron is the whole difference, and it
 * says the row leads somewhere rather than settling anything.
 *
 * An answering row may move the page on, which a `Choice` may not: a radio
 * group keeps its answer on screen, marked, and a question whose set is
 * replaced by the next one has to put its answer where a reader can still see
 * it and change it. A `Folded` line above the step is that place — **so a row
 * that commits belongs only in a step which folds to its answer**, and that is
 * the whole of what `leads={false}` is for. It is not a way to make a button
 * look like a row: one alone in its own list is a single act stated as a line,
 * which is what it should read as.
 *
 * `note` is the thing's own second line, and absent is a fine answer — nothing
 * here invents a sentence about somebody's arrangements.
 *
 * A row whose branch is a page of its own is drawn as that page's link through
 * `render`: an address stays an anchor, so it opens in a tab, is copied, and is
 * followed in place by the site it sits on.
 */
export function BranchRow({
  label,
  note,
  amount,
  disclosure,
  leads = true,
  ...act
}: {
  label: string
  /** The thing's own words, where it has any. */
  note?: ReactNode
  /** A figure the branch names before it is opened — a price, a total. */
  amount?: string
  /** A second, keyboard-operable way to reveal rows belonging to this one. */
  disclosure?: {
    expanded: boolean
    label: string
    onToggle: () => void
  }
  /** False where picking the row answers the question rather than opening a
   *  further one, which is what the chevron announces. */
  leads?: boolean
} & (
  | { onSelect: () => void; render?: never }
  | {
      /** The anchor to draw in place of the `<button>`, wearing the row's
       *  class and holding its contents. An anchor and nothing else: a row
       *  drawn as any other element is one nobody can operate. */
      render: ReactElement<AnchorHTMLAttributes<HTMLAnchorElement>, 'a'>
      onSelect?: never
    }
)) {
  const contents = (
    <>
      <span className="branch-row__text">
        <span className="branch-row__label">{label}</span>
        {note ? <span className="branch-row__note">{note}</span> : null}
      </span>
      {amount ? (
        <span className="branch-row__amount tabular">{amount}</span>
      ) : null}
      {/* The chevron says the row leads somewhere; the label already says
          where, so it is decoration and announces nothing. */}
      {leads ? <ChevronRight className="branch-row__go" aria-hidden /> : null}
    </>
  )
  if (act.render)
    return cloneElement(act.render, {
      className: 'branch-row',
      children: contents,
    })
  if (disclosure)
    return (
      <div className="branch-row">
        <button
          type="button"
          className="branch-row__pick"
          onClick={act.onSelect}
        >
          {contents}
        </button>
        <button
          type="button"
          className="branch-row__disclosure"
          aria-label={disclosure.label}
          aria-expanded={disclosure.expanded}
          onClick={disclosure.onToggle}
        >
          <ChevronDown aria-hidden />
        </button>
      </div>
    )
  return (
    <button type="button" className="branch-row" onClick={act.onSelect}>
      {contents}
    </button>
  )
}

/**
 * The set of branches one question offers, on one hairline stack.
 *
 * A part rather than a second component: the rows are one question, so the
 * legend naming them belongs to the group and not to any row in it.
 *
 * `label` is OPTIONAL, and that is what keeps a question from being asked
 * twice. Where the surface around the rows already states the question — a step
 * that also carries a `Choice` of things you can pick — this draws neither a
 * prompt nor a group, and the rows are plain controls inside whatever names
 * them. Where it is given, the VISIBLE prompt is what names the group, rather
 * than a second copy of the same words in an `aria-label`.
 *
 * `page` is the stack as a published page sets it: open rows on hairlines with
 * the names in the serif, read the way the answers folded above them are read,
 * rather than a bordered box of controls.
 */
export function BranchList({
  label,
  appearance = 'form',
  children,
}: {
  /** The question the rows answer, where these rows are the ones asking it. */
  label?: string
  appearance?: 'form' | 'page'
  children: ReactNode
}) {
  const id = useId()
  const rows = (
    <div
      className="branch-list__rows"
      data-appearance={appearance === 'page' ? 'page' : undefined}
    >
      {children}
    </div>
  )
  if (!label) return rows
  return (
    <div className="branch-list" role="group" aria-labelledby={id}>
      <p className="branch-list__prompt" id={id}>
        {label}
      </p>
      {rows}
    </div>
  )
}
